// VOY-417 §2 — Profile strength.
//
// Completeness % + progress bar from getProfileCompleteness. Below it,
// sharpening prompts for unfilled high-value fields. Each prompt's
// action DEEP-LINKS OUT to the Profile page (VOY-485) — this section
// does not host any editor.
//
// Anchors map to Profile's section IDs (set in the v485 components).
// Profile is a hash-anchor target so /profile#keywords-h scrolls.

import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import type { ProfileCompleteness } from '@/types/profile'

export interface ProfileStrengthSectionProps {
  completeness: ProfileCompleteness | null
  loading: boolean
}

interface Prompt {
  label: string
  hint: string
  /** Profile anchor to deep-link into. Pages preserve scroll-to-id. */
  href: string
  /**
   * When the corresponding breakdown bucket is at or above this
   * threshold, the prompt is hidden — the user has handled it.
   */
  thresholdComplete: boolean
}

function promptsFor(c: ProfileCompleteness | null): Prompt[] {
  if (!c) {
    return [
      { label: 'Complete your basic info', hint: 'Name, phone, DOB', href: '/profile#account-h', thresholdComplete: false },
    ]
  }
  return [
    {
      label: 'Take the travel quiz',
      hint: 'Unlocks your travel DNA — 8 dimensions',
      href: '/profile#dna-h',
      thresholdComplete: c.breakdown.quiz.complete,
    },
    {
      label: 'Add travel preferences',
      hint: 'Budget · Pace · Accommodation · Group · Planning',
      href: '/profile#travel-prefs-h',
      thresholdComplete: c.breakdown.travelPreferences.complete,
    },
    {
      label: 'Add a few keywords',
      hint: 'Up to 5 — tells Voy what matters when you travel',
      href: '/profile#keywords-h',
      thresholdComplete: c.breakdown.keywords.complete,
    },
    {
      label: 'Link a loyalty program',
      hint: 'Voy prioritizes airlines / hotels you already use',
      href: '/profile#loyalty-h',
      thresholdComplete: c.breakdown.loyaltyPrograms.complete,
    },
    {
      label: 'Fill in your basics',
      hint: 'Name, phone, DOB, bio',
      href: '/profile#account-h',
      thresholdComplete: c.breakdown.basicInfo.complete,
    },
  ]
}

export function ProfileStrengthSection({ completeness, loading }: ProfileStrengthSectionProps) {
  const pct = completeness?.overall ?? 0
  const prompts = promptsFor(completeness).filter((p) => !p.thresholdComplete)

  return (
    <section className="card" aria-labelledby="strength-h">
      <div className="card-h">
        <h3 id="strength-h">Profile strength</h3>
        <span className="meta">{loading ? 'LOADING…' : `${pct}% COMPLETE`}</span>
      </div>

      <div className="you-strength-bar" aria-hidden="true">
        <span style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>

      {prompts.length === 0 ? (
        <p className="you-strength-done">
          You&apos;ve filled in everything Voy needs. Take a few searches and the agent will keep tuning itself.
        </p>
      ) : (
        <ul className="you-prompts">
          {prompts.map((p) => (
            <li key={p.label}>
              <Link to={p.href} className="you-prompt">
                <div className="you-prompt-body">
                  <span className="you-prompt-label">{p.label}</span>
                  <span className="you-prompt-hint">{p.hint}</span>
                </div>
                <span className="you-prompt-cta">
                  Add <ArrowRight aria-hidden="true" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
